import React from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    TextField,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { useState, useContext } from "react";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import { Bold, Import } from "lucide-react";
import { ReactComponent as UploadIcon } from "../../../images/uploadIcon.svg";
import { ReactComponent as WhiteUploadIcon } from "../../../images/WhiteUploadIcon.svg";
import { ReactComponent as NoDataIcon } from "../../../images/NoDataCapturedIcon.svg";
import { SearchIcon } from "lucide-react";
import { ReactComponent as DeleteIcon } from "../../../images/APIDeleteNewIcon.svg";
import { color } from "echarts";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import yaml from "js-yaml";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import { useNavigate, useLocation } from "react-router-dom";
import { generateTestCasesForAPI, swaggerToPostman, APITraceabilityMatrix } from "../../../services/webServices";
import MessagePopup from "../../popup-components/MessagePopup";
import { ReactComponent as UploadFileIcon } from "../../../images/uploadedFileIcon.svg";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI.js";
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import { ReactComponent as DownloadIcon } from "../../../images/TraceabilityDownloadIcon.svg";
import { htmlParsingForSwagger } from "../../../services/webServices";
import { ReactComponent as GenerateButtonIcon } from "../../../images/GenerateApiDocIcon.svg";




function AnalyseLiveWebsite() {


    const navigate = useNavigate();
    const location = useLocation();
    const { setLoadingState } = useContext(LoadingModalContext);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");
    const [FileContentEndpoints, setFileContentEndpoints] = useState([]);
    const [traceabilityMatrixData, setTraceabilityMatrixData] = useState({});
    const [apiurl, setApiUrl] = useState("");
    const [responseParsedData, setResponseParsedData] = useState(null);
    const [openAPISpec, setOpenAPISpec] = useState(null);
    const [openAPIDocPopup, setOpenAPIDocPopup] = useState(false);
    const [authoType, setAuthoType] = useState("none");
    const [extractionMethod, setExtractionMethod] = useState("");



    function flattenPostmanItems(items) {
        const flat = [];

        function recurse(subItems) {
            subItems.forEach(item => {
                if (item.request) {
                    flat.push(item); // it's a request
                } else if (item.item && item.item.length > 0) {
                    recurse(item.item); // it's a folder
                }
            });
        }

        recurse(items);
        return flat;
    }


    const extractEndpoints = (openApiSpec) => {
        if (!openApiSpec.paths) return [];

        const endpoints = [];

        Object.entries(openApiSpec.paths).forEach(([path, methods]) => {
            Object.entries(methods).forEach(([method, details]) => {
                endpoints.push({
                    method: method.toUpperCase(),
                    endpoint: path,
                    params: details.parameters
                        ? details.parameters.map((p) => p.name).join(", ")
                        : "-",
                    headers: details.responses?.["200"]?.content
                        ? Object.keys(details.responses["200"].content).join("/n")
                        : "-",
                    authType: details.security
                        ? details.security.flatMap(sec => Object.keys(sec)).join(", ")
                        : "-"


                });
            });
        });

        return endpoints;
    };


    const handleGenerateOpenAPISpec = async () => {
        let progressHandler;
        try {
            progressHandler = handleProgressBar({ setLoadingState, totalDurationMs: 9000 });
            progressHandler.start("Generating API Document");
            const response = await htmlParsingForSwagger({ url: apiurl });
            await progressHandler.finishAndClose();

            if (response && response.data) {
                setResponseParsedData(response.data);
                setOpenAPISpec(response.data.spec);
                setOpenAPIDocPopup(true)
                const endpoints = extractEndpoints(response.data.spec);
                setFileContentEndpoints(endpoints);

            }

        } catch (error) {
            console.error("Error generating OpenAPI spec:", error);
            setLoadingState({ open: false, message: "", progress: null });
            setPopupMessage("Error generating OpenAPI spec. Please check the API URL and try again.");
            setPopupOpen(true);
        } finally {
            console.log("done"); // Reset the input field after processing
            if (progressHandler) {
                progressHandler.stop();
            }
            setLoadingState({ open: false, message: "", progress: null });
        }
    }

    const handleGenerateTestCases = async () => {
        let progressHandler;
        try {
            let tcCounter = 1;
            const finalArray = [];
            let traceabilityMatrix = null;
            // const responseParsedData = await htmlParsingForSwagger({ url: apiurl });
            // if (responseParsedData && responseParsedData.data) {
            //   console.log("Parsed Data:", responseParsedData.data);
            //   // Handle the parsed data as needed
            //   setOpenAPISpec(responseParsedData.data.spec);
            progressHandler = handleProgressBar({ setLoadingState, totalDurationMs: 420000 })

            const responsePostmanCollection = await swaggerToPostman(openAPISpec);
            if (responsePostmanCollection.data.item) {
                const flattenedItems = flattenPostmanItems(responsePostmanCollection.data.item);
                if (flattenedItems.length == 1) {
                    progressHandler.start("Generating Test Cases");
                }
                else {
                    progressHandler.start("Generating Test Cases for Postman Folder 1");
                }

                const promises = flattenedItems.map(async (item, index) => {
                    progressHandler.updateMessage(`Generating Test Cases for Postman Folder ${index + 1}`);
                    try {
                        const testcasesresponse = await generateTestCasesForAPI(item);

                        if (testcasesresponse.data && testcasesresponse.data.testcases) {

                            return testcasesresponse.data.testcases;

                        }
                    } catch (error) {
                        console.log(
                            error?.response?.data?.error || error.message,
                            "Error processing swagger item:", index
                        );
                    }
                    return [];
                }
                );
                const results = await Promise.allSettled(promises);
                for (const result of results) {
                    if (result.status === "fulfilled" && Array.isArray(result.value)) {
                        const updated = result.value.map((testcase) => {
                            const isFunctional = testcase.Category?.toLowerCase() === "functional testing";
                            const subCategory = testcase["Sub-Category"]?.toLowerCase() || "";

                            let prefix = isFunctional ? "TCD_FT" : "TCD_NFT";
                            let shortCode = "";

                            if (subCategory.includes("positive")) shortCode = "POS";
                            else if (subCategory.includes("negative")) shortCode = "NEG";
                            else if (subCategory.includes("boundary")) shortCode = "BOUND";
                            else if (subCategory.includes("error")) shortCode = "ERR";
                            else if (subCategory.includes("security")) shortCode = "SEC";
                            else if (subCategory.includes("performance")) shortCode = "PERF";

                            const padded = String(tcCounter).padStart(2, "0");
                            const id = `${prefix}_${padded}_${shortCode}`;
                            tcCounter++;

                            return {
                                ...testcase,
                                "Test Case ID": id,
                            };
                        });

                        finalArray.push(...updated);
                    }
                }


                // const filteredTestCases = finalArray.map((testcase) => ({
                //     "Test Case ID": testcase["Test Case ID"],
                //     "API Endpoint": testcase["API Endpoint"],
                //     "Method": testcase["Method"],
                //     "Test Description": testcase["Test Description"],
                //     "Category": testcase["Category"],
                // }));

                // try {
                //     progressHandler.updateMessage("Generating Traceability Matrix");
                //     const response = await APITraceabilityMatrix(filteredTestCases);
                //     if (response.data !== null) {
                //         traceabilityMatrix = response.data;
                //     }
                // } catch (error) {
                //     console.log(
                //         error?.response?.data?.error || error.message,
                //         "Error generating traceability matrix"
                //     );
                //     setPopupMessage("Error generating traceability matrix. Please try again.");
                //     setPopupOpen(true);
                // }

                await progressHandler.finishAndClose();
                navigate("/dashboard/api-testing/test-cases-display", {
                    state: {
                        ...location.state,
                        generateTestCasesData: finalArray,
                        apiDocData: openAPISpec || null,
                        button: "urlHTMLSwaggerParser",

                    },
                });


            }
        } catch (error) {
            console.error("Error generating test cases:", error);
            setLoadingState({ open: false, message: "", progress: null });
            setPopupMessage("Something went wrong while generating test cases.");
            setPopupOpen(true);
        } finally {
            console.log("done"); // Reset the input field after processing
            if (progressHandler) {
                progressHandler.stop();
                setLoadingState({ open: false, message: "", progress: null });
            }
        }
    }

    const handleDownloadAPIDocument = (openAPISpec) => {
        if (!openAPISpec) return;

        try {
            const yamlString = yaml.dump(openAPISpec);
            const blob = new Blob([yamlString], { type: "text/yaml" });
            const url = URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = "openapi-document.yaml";
            document.body.appendChild(a);
            a.click();

            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error converting OpenAPI document to YAML:", error);
        }
    };



    return (
        <Box
            sx={{
                p: 2,
                bgcolor: "#F6F3F2",
                display: "flex",
                flexDirection: "column",
                height: "100%",      // fill whatever space parent gave
                maxHeight: "100%",   // don’t exceed it
                minHeight: 0,        // <-- key: allows child to shrink for scrolling
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    // height: { xs: "60vh", sm: "65vh", md: "73vh" }, // 

                    overflowY: "scroll", // scrolling works
                    scrollbarWidth: "none", // Firefox
                    "&::-webkit-scrollbar": {
                        display: "none", // Chrome, Safari, Edge
                    },  // adds scroll if content is taller
                    // mx: "auto",
                    mb: 1
                }}
            >
                <Box sx={{ justifyContent: "space-between", display: "flex", gap: 2 }}>
                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            p: 2,
                            flex: 1,
                            mb: 3,
                            border: "none",
                            backgroundColor: "#fff",


                        }}
                    >
                        <Typography variant="Subtitle1" fontWeight="bold" fontSize={17}>Analyse Live Website</Typography>
                        {/* Upload Specifications Section */}

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1.5 }}>

                            Automatically discover and analyze APIs from running applications using advanced traffic analysis </Typography>
                        <TextField
                            label="API Specification URL"
                            variant="outlined"
                            fullWidth
                            value={apiurl}
                            onChange={(e) => setApiUrl(e.target.value)}
                            placeholder="https://api.example.com/swagger.json"
                            sx={{ mb: 3 }}
                        >

                        </TextField>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                            <FormControl fullWidth>
                                <InputLabel >Discovery Method</InputLabel>
                                <Select
                                    label="Discovery Method"
                                    placeholder="Select"
                                    defaultValue="none"
                                    value={authoType}
                                    onChange={(e) => setAuthoType(e.target.value)}
                                >
                                    <MenuItem value="none">None</MenuItem>
                                    <MenuItem value="apiKey">API Key</MenuItem>
                                    <MenuItem value="oauth2">OAuth 2.0</MenuItem>
                                    <MenuItem value="basic">Basic Auth</MenuItem>

                                </Select>
                            </FormControl>


                            <FormControl fullWidth>
                                <InputLabel >Extraction Method</InputLabel>
                                <Select
                                    label="Extraction Method"
                                    placeholder="Select"
                                    defaultValue="none"
                                    value={extractionMethod}
                                    onChange={(e) => setExtractionMethod(e.target.value)}
                                    sx={{ mb: 3 }}
                                >
                                    <MenuItem value="none">None</MenuItem>
                                    <MenuItem value="apiKey">API Key</MenuItem>
                                    <MenuItem value="oauth2">OAuth 2.0</MenuItem>
                                    <MenuItem value="basic">Basic Auth</MenuItem>

                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ display: "flex", gap: 2 }}>

                            <Button variant="outlined" startIcon={<GenerateButtonIcon />} sx={{
                                borderRadius: "6px", color: "#0094FB", borderColor: " #0094FB",
                                borderWidth: "2px",
                                fontWeight: "bold", textTransform: "none"
                            }}
                                onClick={handleGenerateOpenAPISpec}
                            >
                                Generate API Document
                            </Button>

                            <Button variant="outlined" startIcon={<DownloadIcon />} sx={{
                                borderRadius: "6px", color: "#0094FB", borderColor: " #0094FB",
                                borderWidth: "2px",
                                fontWeight: "bold", textTransform: "none"
                            }}
                                disabled={!openAPISpec}
                                onClick={() => handleDownloadAPIDocument(openAPISpec)}
                            >

                                Download API Document
                            </Button>

                        </Box>

                    </Card>

                </Box>

                {/* Captured API Endpoints */}
                <Box  >
                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            p: 2,
                            mb: 3,
                            border: "none"
                        }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 30 }} >
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, fontSize: 17 }}>
                                Captured API Endpoints
                            </Typography>
                            <TextField
                                InputProps={{
                                    startAdornment: (
                                        <SearchIcon style={{ color: "#a9a9a9", marginRight: 8 }} />
                                    ),
                                }}
                                placeholder="Search by endpoint"
                                variant="outlined"

                                size="small"
                                sx={{ mb: 2, width: 500 }}
                            />
                        </Box>
                        {/* <Divider sx={{ mb: 2 }} /> */}

                        {/* Empty State */}
                        <Box
                            sx={{
                                textAlign: "center",
                                py: 3,
                                color: "text.secondary",
                                minHeight: 200,
                                overflowX: "auto",
                                overflowY: "auto",

                            }}

                        >
                            {/* <NoDataIcon /> */}

                            {FileContentEndpoints && FileContentEndpoints.length > 0 ? (
                                <Table stickyHeader sx={{ border: '1px solid #e0e0e0', borderLeft: 'none', borderRight: 'none' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><b>HTTP Method</b>
                                                <ArrowDownwardOutlinedIcon sx={{
                                                    fontSize: "medium", ml: 0.3, position: "relative",
                                                    top: "3px"
                                                }} />
                                            </TableCell>
                                            <TableCell><b>Endpoint</b>
                                                <ArrowDownwardOutlinedIcon sx={{
                                                    fontSize: "medium", ml: 0.3, position: "relative",
                                                    top: "3px"
                                                }} />
                                            </TableCell>
                                            <TableCell><b>Params</b>
                                                <ArrowDownwardOutlinedIcon sx={{
                                                    fontSize: "medium", ml: 0.3, position: "relative",
                                                    top: "3px"
                                                }} />
                                            </TableCell>
                                            <TableCell><b>Headers</b>
                                                <ArrowDownwardOutlinedIcon sx={{
                                                    fontSize: "medium", ml: 0.3, position: "relative",
                                                    top: "3px"
                                                }} />
                                            </TableCell>
                                            <TableCell><b>Auth Type</b>
                                                <ArrowDownwardOutlinedIcon sx={{
                                                    fontSize: "medium", ml: 0.3, position: "relative",
                                                    top: "3px"
                                                }} />
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {FileContentEndpoints.map((row, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell sx={{ minWidth: 200 }}>{row.method}</TableCell>
                                                <TableCell sx={{ color: "text.secondary", minWidth: 200 }}>{row.endpoint}</TableCell>
                                                <TableCell sx={{ color: "text.secondary", minWidth: 200 }}>{row.params}</TableCell>
                                                <TableCell sx={{ color: "text.secondary", minWidth: 200 }}>{row.headers}</TableCell>
                                                <TableCell sx={{ color: "text.secondary", minWidth: 200 }}>{row.authType}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <Box textAlign="center" py={6} color="text.secondary">
                                    <NoDataIcon />
                                </Box>
                            )}


                        </Box>
                    </Card>
                </Box>
            </Box>

            {/* Bottom Buttons */}
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button variant="outlined" sx={{
                    borderRadius: "8px", color: "#0094FB", borderColor: " #0094FB",
                    borderWidth: "2px",   // makes border bold
                    fontWeight: "bold", textTransform: "none"
                }}>
                    <ArrowBackIcon style={{ marginRight: 6, fontSize: 18 }} />
                    Back
                </Button>
                <Button
                    variant="contained"
                    sx={{
                        borderRadius: "8px",
                        backgroundColor: "#0094FB",
                        textTransform: "none",
                        fontWeight: "bold",
                    }}
                    disabled={!openAPISpec}
                    onClick={handleGenerateTestCases}

                >
                    Generate Test Cases
                    <ArrowForwardIcon style={{ marginLeft: 8, fontSize: 18 }} />
                </Button>
            </Box>
            <MessagePopup
                modalOpen={popupOpen}
                message={popupMessage}
                okayHandler={() => setPopupOpen(false)}
            />
        </Box>
    );
}

export default AnalyseLiveWebsite;